import React from 'react'
import { NumericFormat } from 'react-number-format'
import { kebabCase, startCase, lowerCase } from 'lodash'

/**
 * Provides a way to format a number to a currency string.
 * @param {Ref} inputRef - The ref to the input element.
 * @returns {string} The formatted currency string.
 */
export const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props

  return (
    <NumericFormat
      allowNegative={false}
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator
      prefix="$"
    />
  )
})

export const formatCurrency = (number = 0) => {
  // convert to number
  number = Number(number)

  if (isNaN(number)) return '$0'

  // add a dollar sign and commas
  return `$${number
    .toFixed(0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

export const capitalizeFirstLetter = (string) => {
  let lowered = string?.toLowerCase()
  return lowered?.replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
    letter?.toUpperCase()
  )
}

export const formatDetailPagePath = (address, mlsId, boardId) => {
  // join the streetName, zip after checking for empty or null values and convert to lowercase then kebab case
  const addr = kebabCase(
    [
      address?.unitNumber,
      address?.streetNumber,
      address?.streetName,
      address?.streetSuffix,
      address?.streetDirection,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  )

  // join, addr and zip after checking for empty or null values and convert to lowercase
  const addressWithZip = [addr, address?.zip]
    .filter(Boolean)
    .join('-')
    .toLowerCase()

  // create path /detail/:state/:city/:neighborhood/:address/:mlsId/:boardId
  const path = [
    '/detail',
    address?.state,
    address?.city,
    address?.neighborhood,
    addressWithZip,
    mlsId,
    boardId,
  ]
    .filter(Boolean)
    .join('/')

  return path
}

/**
 * Generates SEO-optimised listing copy based on available data points.
 * Returns a short metaDescription (120–155 chars) and a slightly longer
 * pageCopy for the on-page "About" section.
 *
 * Variant selection (deterministic per listing):
 *   A – neighborhood + beds + baths available
 *   B – sqft + beds + baths (no neighborhood)
 *   C – beds + baths only
 *   D – fallback (price + type only)
 */
export const generateListingCopy = (data, mlsId) => {
  const address = (formatListingName(data.address) || '').trim().replace(/\s+/g, ' ')
  const city = data.address.city || ''
  const province = data.address.state || ''
  const rawNeighborhood = data.address.neighborhood || ''
  // Avoid "Neighbourhood, City" when they are the same value
  const neighborhood =
    rawNeighborhood && rawNeighborhood !== city ? rawNeighborhood : ''

  const propertyType =
    data.details.propertyType === 'Other' || !data.details.propertyType
      ? 'property'
      : data.details.propertyType

  const price = formatCurrency(parseFloat(data.listPrice))
  const beds = data.details.numBedrooms >= 1 ? data.details.numBedrooms : 0
  const baths = data.details.numBathrooms >= 1 ? data.details.numBathrooms : 0
  const sqft =
    data.details.sqft && data.details.sqft !== '' ? data.details.sqft : null
  const isLease = data.type === 'Lease'

  const hasNeighborhood = !!neighborhood
  const hasBeds = beds >= 1
  const hasBaths = baths >= 1
  const hasSqft = !!sqft

  let variant
  if (hasNeighborhood && hasBeds && hasBaths) variant = 'A'
  else if (hasSqft && hasBeds && hasBaths) variant = 'B'
  else if (hasBeds && hasBaths) variant = 'C'
  else variant = 'D'

  const priceClause = isLease ? `for lease at ${price}` : `at ${price}`
  const listedAt = isLease
    ? `Available for lease at ${price}`
    : `Listed at ${price}`

  let metaDescription
  let pageCopy

  switch (variant) {
    case 'A':
      metaDescription = `Explore ${address} in ${neighborhood}, ${city}. ${beds}-bed, ${baths}-bath ${propertyType} ${priceClause} — view photos and full listing details.`
      pageCopy = `${address} is a ${beds}-bedroom, ${baths}-bathroom ${propertyType} in ${neighborhood}, ${city}, ${province}. ${listedAt} (MLS®#${mlsId}), this is a great opportunity to explore one of ${city}'s sought-after neighbourhoods.`
      break
    case 'B':
      metaDescription = `View ${address} in ${city}, ${province}. ${sqft} sqft ${propertyType} ${priceClause}. Browse photos, pricing, and property details.`
      pageCopy = `${address} is a ${beds}-bedroom, ${baths}-bathroom ${propertyType} in ${city}, ${province}, offering ${sqft} sqft of living space. ${listedAt} with MLS®#${mlsId}.`
      break
    case 'C':
      metaDescription = `Discover ${address} in ${city}, ${province}. ${beds}-bed, ${baths}-bath ${propertyType} ${priceClause}. See photos and full listing details today.`
      pageCopy = `${address} is a ${beds}-bedroom, ${baths}-bathroom ${propertyType} in ${city}, ${province}. With an asking price of ${price}, MLS®#${mlsId} represents solid value in the ${city} real estate market.`
      break
    default:
      metaDescription = `${propertyType} at ${address} in ${city}, ${province}. Listed at ${price}—see photos, pricing, and full details on Homewise Real Estate.`
      pageCopy = `${address} is a ${propertyType} available in ${city}, ${province}. Listed at ${price} with MLS®#${mlsId}—contact us for full property details and to arrange a showing.`
  }

  return { metaDescription, pageCopy }
}

export const formatListingName = (address) => {
  let name = ''

  // if address.streetNumber, address.streetName, address.streetSuffix, address.streetDirection are not null or empty use that for the name
  if (address?.streetNumber && address?.streetName) {
    name = `${address?.unitNumber ? `${address?.unitNumber} - ` : ''} ${
      address?.streetNumber
    } ${startCase(lowerCase(address?.streetName))} ${
      address?.streetSuffix || ''
    } ${address?.streetDirection || ''}`
    return name
  }

  if (address?.streetName) {
    // split the street name into an array, then remove #, -, from the values
    const streetName = address?.streetName?.split(' ')?.map((item) => {
      return item?.replace(/[#-]/g, '')
    })

    // if the first 2 items are numbers, then the first item is the unit number
    if (!isNaN(Number(streetName[0])) && !isNaN(Number(streetName[1]))) {
      name = `${streetName[0]} - ${streetName[1]} ${startCase(
        lowerCase(streetName?.slice(2)?.join(' '))
      )}`
    } else {
      name = startCase(lowerCase(streetName?.join(' ')))
    }

    return name
  }
}
